import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

// Components
import GatsbyHead from '../../components/layout/gatsby-head';
import Assemblies from '../../components/assemblies';
import Layout, { Container, TwoThirds, SideBar } from '../../components/layout';
import PageTitle from '../../components/page-title';
import RichText from '../../components/rich-text';
import SubpageMenu from '../../components/advice-page/subpage-menu';
import SubpagePagination from '../../components/advice-page/subpage-pagination';
import BounceCard from '../../components/bounce-card';
import ChildPageLink from '../../components/advice-page/child-page-link';
import NarrationWrapper from '../../components/narration';
import withSplitTest from '../../components/with-split-test';

// Styles
import { ContentWithSideBar } from '../../components/styled/containers';

// Utils
import { dateAsString } from '../../utils/dates';
import { extractLayoutProps } from '../../utils/layout';
import { useResearchBanner } from '../../hooks/research-banner';
import { useSurvey } from '../../hooks/survey';

/**
 * This page can be displayed in two ways: guide (or child of guide), and standard (or child of standard)
 *
 * A guide takes the children, and displays an ordered menu with Prev/Next links between them. The parent
 * page acts as the landing page.
 *
 * A standard page displays an (unordered) list of child pages that do not have navigation between them.
 *
 * Breadcrumbs are used across all pages.
 *
 * If a child of a guide page is itself a guide then it's own child pages are displayed in the standard manner,
 * but those children are displayed as children-of-guides.
 */

/*
 * This is a temporary solution to enable the narration prototype on a whole section of pages.
 */

export const Head = ({ data }) => <GatsbyHead pageContent={data.page} />;

const enableNarration = () => false; // Disables narration for all environments

const AdvicePage = ({ data }) => {
  const { page } = data;

  const {
    assemblies,
    title,
    pageOneTitle,
    bodyCopy,
    sidebarAssemblies,
    displayBounceCard,
    lastAmended,
    displayAsGuide,
    guideIntroAssemblies,
    hideChildren = false,
    showQuickExitButton,
  } = page;

  const navigation = page.navigation || {};

  const { advicePageAssemblies, advicePageSidebarAssemblies } =
    data.contentfulConfig;

  const embeddedSurvey = useSurvey(navigation.path);

  /* This uses the showShareYourStoryBanner bool as set in Contentful to either show or hide
  a banner which was previously shown on all advice pages by default */
  const showShareYourStoryBanner = page.showShareYourStoryBanner ?? true;

  const SHOW_YOUR_STORY_BANNER_ID = '1KmqvWu7MpiXjesDOqTXKp';

  const filteredAssemblies = showShareYourStoryBanner
    ? advicePageAssemblies
    : advicePageAssemblies.filter(
        ({ contentful_id: id }) => id !== SHOW_YOUR_STORY_BANNER_ID
      );

  const researchBanner = useResearchBanner(navigation.path);

  const partOfGuide = navigation.up?.page?.displayAsGuide;

  /*
  This template varies between the England and Scotland sites.
  The England site treats the top level page as essentially the same as the child pages.
  As such it is accessible via the pagination and the sub page menu
  */
  const guidesConfig = {
    subPageMenuItems: {
      scotland:
        displayAsGuide && !partOfGuide ? navigation.down : navigation.siblings,
      england:
        // If we are on the top level page show the top level page as first in the list followed by its child pages
        // but if we are on any other page show the top level page followed by the current page and its siblings
        displayAsGuide && navigation.down.length
          ? [
              {
                label: pageOneTitle || title,
                path: navigation.path,
              },
              ...navigation.down,
            ]
          : [
              {
                label: navigation.up.page.pageOneTitle || navigation.up.label,
                path: navigation.up.path,
              },
              ...navigation.siblings,
            ],
    },
    paginationPrevious: {
      scotland: navigation.previous,
      // if navigation.previous doesn't exist we must be on the first of the child pages so display the top level page as the previous page
      england: navigation.previous || {
        label: navigation.up.page.pageOneTitle || navigation.up.label,
        path: navigation.up.path,
      },
    },
  };

  let pageTitle = title;
  // eslint-disable-next-line prefer-destructuring
  let guideIndexHeading = page.guideIndexHeading;

  // If page is part of a guide use the guide overview/parent page as the top page title
  if (partOfGuide) {
    pageTitle = navigation.up?.label;
    guideIndexHeading = navigation.up?.page?.guideIndexHeading;
  }

  const showSubTitle = () => {
    // Use `pageOneTitle` if we're on page one
    const guideSubTitle = displayAsGuide ? pageOneTitle : title;

    // if there is no subtitle
    if (!guideSubTitle) {
      return null;
    }

    // if it's a scotland page and it is a top level page
    if (process.env.SHELTER_SITE === 'scotland' && displayAsGuide) {
      return null;
    }
    // if it's not a guide page or a part of a guide
    if (!displayAsGuide && !partOfGuide) {
      return null;
    }

    return <h1>{guideSubTitle}</h1>;
  };

  return (
    <Layout
      showQuickExitButton={showQuickExitButton}
      {...extractLayoutProps(page, data.contentfulConfig)}
    >
      {researchBanner}
      <article>
        <PageTitle twoThirds>
          <h1>{pageTitle}</h1>
        </PageTitle>
        <Container>
          <ContentWithSideBar>
            <TwoThirds>
              {(partOfGuide || displayAsGuide) && (
                <>
                  <Assemblies assemblies={guideIntroAssemblies} />
                  <SubpageMenu
                    title={guideIndexHeading || 'Steps in this guide'}
                    items={
                      guidesConfig.subPageMenuItems[process.env.SHELTER_SITE]
                    }
                    currentPath={navigation.path}
                  />
                </>
              )}
              {showSubTitle()}
              {enableNarration(navigation.path) ? (
                <NarrationWrapper>
                  {bodyCopy && <RichText richText={bodyCopy} />}

                  <Assemblies assemblies={assemblies} />
                </NarrationWrapper>
              ) : (
                <>
                  {bodyCopy && <RichText richText={bodyCopy} />}
                  <Assemblies assemblies={assemblies} />
                </>
              )}
              {!hideChildren && (
                <>
                  {(!displayAsGuide || (displayAsGuide && partOfGuide)) &&
                  navigation.down?.length
                    ? navigation.down.map((d) => (
                        <ChildPageLink
                          title={d.label}
                          path={d.path}
                          description={
                            d.page?.pageInformation?.shortDescription
                              ?.shortDescription
                          }
                        />
                      ))
                    : null}

                  {displayAsGuide && !partOfGuide && (
                    <SubpagePagination next={navigation.down?.[0]} />
                  )}
                </>
              )}
              {partOfGuide && (
                <SubpagePagination
                  next={navigation.next}
                  previous={
                    guidesConfig.paginationPrevious[process.env.SHELTER_SITE]
                  }
                />
              )}

              {lastAmended && (
                <p>Last updated: {dateAsString(lastAmended, 'D MMMM YYYY')}</p>
              )}

              <div id="hotjar-embedded-survey-original" />
              {embeddedSurvey}
              <Assemblies assemblies={filteredAssemblies} />
            </TwoThirds>
            <SideBar>
              {displayBounceCard && <BounceCard />}
              <Assemblies
                assemblies={[
                  ...(sidebarAssemblies || []),
                  ...(advicePageSidebarAssemblies || []),
                ]}
              />
            </SideBar>
          </ContentWithSideBar>
        </Container>
      </article>
    </Layout>
  );
};

AdvicePage.propTypes = {
  data: PropTypes.shape({
    page: PropTypes.object,
    contentfulConfig: PropTypes.object,
  }),
};

export default withSplitTest(AdvicePage);

export const advicePageQuery = graphql`
  query advicePageTemplateQuery($contentful_id: String!) {
    page: contentfulPageAdvice(contentful_id: { eq: $contentful_id }) {
      ...PageAdviceFragment
    }

    contentfulConfig(name: { eq: "config" }) {
      advicePageAssemblies {
        ...AdvicePageAssembliesFragment
      }
      advicePageSidebarAssemblies {
        ...SidebarFragment
      }
    }
    ...NavigationFragment
  }
`;
